import {asyncScheduler, firstValueFrom, Observable, observeOn, PartialObserver, Subscriber} from 'rxjs';
import {DestroyRef, effect, EffectRef, inject, Injector, Signal, untracked} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter} from 'rxjs/operators';

/**
 * Lifecycle-safe subscription helper for Angular components/directives.
 *
 * Supports:
 * - Observable<void> or Observable<T>
 * - Callback function (next)
 * - PartialObserver (next, error, complete)
 */
export function onEach<T = void>(
    observable: Observable<T>,
    observer: ((value: T) => void) | PartialObserver<T>,
    destroyRef: DestroyRef = inject(DestroyRef)
): void {
    observable.pipe(takeUntilDestroyed(destroyRef)).subscribe(observer);
}

/**
 * Returns a promise that resolves when an observable emits a value matching the predicate.
 *
 * Uses asyncScheduler to defer emission processing, ensuring that any synchronous code
 * (such as state changes) runs before checking the predicate. This is useful for waiting
 * until the NEXT matching emission rather than resolving immediately if the current value
 * already matches.
 *
 * Typical use case: Waiting for a BehaviorSubject to transition to a specific state,
 * even if it's currently in that state but about to change.
 *
 * @param observable - The observable to watch
 * @param predicate - Function returning true when the promise should resolve
 * @returns Promise that resolves with the matching value
 */
export function whenObservableMatches<T>(
    observable: Observable<T>,
    predicate: (value: T) => boolean
): Promise<T> {
    return firstValueFrom(observable.pipe(
        observeOn(asyncScheduler),
        filter(predicate)
    ));
}

/**
 * Converts a signal to an observable that emits whenever the signal value changes.
 * The observable emits the current signal value immediately upon subscription,
 * then emits again whenever the signal changes.
 *
 * @param signal - The signal to convert to an observable
 * @param injector - Optional injector for running the effect (defaults to current injector)
 * @returns Observable that emits the signal's values
 */
export function toObservable<T>(
    signal: Signal<T>,
    injector: Injector = inject(Injector)
): Observable<T> {
    return new Observable<T>((subscriber: Subscriber<T>) => {
        const effectRef: EffectRef = effect(() => {
            const value = signal();
            untracked(() => subscriber.next(value));
        }, {injector});
        return () => effectRef.destroy();
    });
}
